import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { downloadBlob } from "../lib/api";
import { PageHeader } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CaretDown, CaretRight, FileXls, FloppyDisk, PaperPlaneTilt, CheckCircle, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { useAiConfirm } from "../components/AiConfirmDialog";

const STATUS_STYLE = {
  draft: "bg-gray-100 text-gray-700 border-gray-300",
  submitted: "bg-blue-100 text-blue-800 border-blue-300",
  cop_received: "bg-emerald-100 text-emerald-800 border-emerald-300",
  archived: "bg-slate-100 text-slate-600 border-slate-300",
};
const STATUS_LABEL = {
  draft: "Draft",
  submitted: "Submitted to RE",
  cop_received: "COP received",
  archived: "Archived",
};

export default function RiskAssessmentEditor() {
  const { confirmAi, aiConfirmDialog } = useAiConfirm();
  const { id } = useParams();
  const nav = useNavigate();
  const [sections, setSections] = useState([]);
  const [ira, setIra] = useState(null);
  const [open, setOpen] = useState({});
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [sc, doc] = await Promise.all([
          api.get("/risk-assessments/schema"),
          api.get(`/risk-assessments/${id}`),
        ]);
        setSections(sc.data.sections || []);
        setIra(doc.data);
        // Open the first section by default.
        setOpen({ [sc.data.sections?.[0]?.key]: true });
      } catch (e) {
        toast.error("Could not load IRA");
        nav("/risk-assessments");
      }
    })();
  }, [id, nav]);

  const canEdit = ira && ira.status !== "archived";

  const setAnswer = (key, val) => {
    setIra((prev) => ({ ...prev, answers: { ...(prev.answers || {}), [key]: val } }));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const saveSection = async (key) => {
    setBusy(`save-${key}`);
    try {
      await api.put(`/risk-assessments/${id}/section`, {
        section: key, body: ira.answers?.[key] || "",
      });
      setDirty((d) => ({ ...d, [key]: false }));
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };

  const saveAll = async () => {
    setBusy("save-all");
    try {
      for (const s of sections) {
        if (dirty[s.key]) {
          await api.put(`/risk-assessments/${id}/section`, {
            section: s.key, body: ira.answers?.[s.key] || "",
          });
        }
      }
      setDirty({});
      toast.success("All sections saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };

  const changeStatus = async (next) => {
    setBusy("status");
    try {
      await api.put(`/risk-assessments/${id}/cop`, {
        cop_notes: ira.cop_notes || "", status: next,
      });
      const { data } = await api.get(`/risk-assessments/${id}`);
      setIra(data);
      toast.success(`Status → ${STATUS_LABEL[next] || next}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status change failed");
    } finally { setBusy(""); }
  };

  const saveCop = async () => {
    setBusy("cop");
    try {
      await api.put(`/risk-assessments/${id}/cop`, {
        cop_notes: ira.cop_notes || "",
      });
      toast.success("COP notes saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "COP save failed");
    } finally { setBusy(""); }
  };

  const exportXlsx = async () => {
    setBusy("export");
    try {
      await downloadBlob(`/risk-assessments/${id}/export.xlsx`, `IRA-${id.slice(0, 8)}.xlsx`);
    } finally { setBusy(""); }
  };

  const refreshScope = async () => {
    setBusy("refresh-scope");
    try {
      const { data } = await api.post(`/risk-assessments/${id}/refresh-scope`);
      const doc = await api.get(`/risk-assessments/${id}`);
      setIra(doc.data);
      toast.success(`Scope refreshed — ${data.distinct_standards} standards • ${data.count} requirements (was ${data.previous_count})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scope refresh failed");
    } finally { setBusy(""); }
  };

  const suggestAll = async () => {
    if (!(await confirmAi())) return;
    setBusy("suggest-all");
    try {
      const { data } = await api.post(`/risk-assessments/${id}/suggest-all?confirm_confidential=1`);
      if (data.queued === 0) {
        toast.info(data.note || "All sections already have SME content");
      } else {
        toast.success(`AI queued ${data.queued} sections — drafting in the background (~15s each)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch draft failed");
    } finally { setBusy(""); }
  };

  // Live-poll progress + refresh the IRA doc while a batch is in flight.
  useEffect(() => {
    if (!ira) return undefined;
    const p = ira.suggest_all_progress;
    if (!p || p.done + p.errors >= p.queued) return undefined;
    const tick = setInterval(async () => {
      try {
        const { data } = await api.get(`/risk-assessments/${id}`);
        setIra((prev) => ({ ...data, cop_notes: prev?.cop_notes ?? data.cop_notes }));
        const pp = data.suggest_all_progress;
        if (pp && pp.done + pp.errors >= pp.queued) {
          clearInterval(tick);
          toast.success(`AI batch complete — ${pp.done} drafted${pp.errors ? `, ${pp.errors} errors` : ""}`);
        }
      } catch { /* silent — poll again */ }
    }, 8000);
    return () => clearInterval(tick);
  }, [ira, id]);

  const suggestSection = async (key) => {
    if (!(await confirmAi())) return;
    setBusy(`suggest-${key}`);
    try {
      const { data } = await api.post(`/risk-assessments/${id}/suggest/${key}?confirm_confidential=1`);
      if (data.overwrote_existing) {
        setIra((prev) => ({ ...prev, answers: { ...(prev.answers || {}), [key]: data.draft } }));
        toast.success("AI draft applied — edit it to your specifics");
      } else {
        // SME already had substantial content — show a soft preview toast, don't clobber.
        toast.info("Existing content preserved. Clear the section to accept an AI draft.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI draft failed");
    } finally { setBusy(""); }
  };

  const completed = useMemo(() => {
    if (!ira) return 0;
    return sections.filter((s) => (ira.answers?.[s.key] || "").trim().length > 20).length;
  }, [ira, sections]);

  if (!ira) return null;

  const totalReqs = (ira.applicability_snapshot || []).length;
  const distinctStds = new Set((ira.applicability_snapshot || []).map((x) => x.standard_id)).size;

  return (
    <div className="space-y-4">
      {aiConfirmDialog}
      <div className="flex items-start justify-between gap-4">
        <PageHeader title={ira.name}
          subtitle={`Applicable snapshot: ${distinctStds} standards • ${totalReqs} requirements — locked in at IRA creation for a stable assessment window.`} />
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs border px-2 py-1 rounded ${STATUS_STYLE[ira.status] || STATUS_STYLE.draft}`} data-testid="ira-status">
            {STATUS_LABEL[ira.status] || ira.status}
          </span>
          {ira.status === "draft" && canEdit && (
            <Button onClick={refreshScope} disabled={busy === "refresh-scope"}
                    variant="outline" data-testid="ira-refresh-scope-btn"
                    title="Re-snapshot the applicable Standards & Requirements from the current entity profile">
              <ArrowsClockwise size={16} className="mr-1.5" />
              {busy === "refresh-scope" ? "Refreshing…" : "Refresh scope"}
            </Button>
          )}
          {canEdit && (
            <Button onClick={suggestAll} disabled={busy === "suggest-all"}
                    variant="outline" data-testid="ira-suggest-all-btn">
              <Sparkle size={16} className="mr-1.5" />
              {busy === "suggest-all" ? "Queuing…" : "Draft all"}
            </Button>
          )}
          {canEdit && (
            <Button onClick={saveAll} disabled={busy === "save-all" || Object.values(dirty).every((v) => !v)}
                    variant="outline" data-testid="ira-save-all-btn">
              <FloppyDisk size={16} className="mr-1.5" /> Save all
            </Button>
          )}
          {ira.status === "draft" && (
            <Button onClick={() => changeStatus("submitted")} disabled={busy === "status"} data-testid="ira-submit-btn">
              <PaperPlaneTilt size={16} className="mr-1.5" /> Mark submitted
            </Button>
          )}
          {ira.status === "submitted" && (
            <Button onClick={() => changeStatus("cop_received")} disabled={busy === "status"} data-testid="ira-cop-received-btn">
              <CheckCircle size={16} className="mr-1.5" /> COP received
            </Button>
          )}
          <Button onClick={exportXlsx} disabled={busy === "export"} variant="outline" data-testid="ira-export-btn">
            <FileXls size={16} className="mr-1.5" /> Export .xlsx
          </Button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground flex items-center gap-3" data-testid="ira-progress">
        <span>Progress: <span className="font-mono font-semibold">{completed}/{sections.length}</span> sections started</span>
        {ira.suggest_all_progress && (ira.suggest_all_progress.done + ira.suggest_all_progress.errors) < ira.suggest_all_progress.queued && (
          <span className="inline-flex items-center gap-1.5 text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded" data-testid="ira-batch-chip">
            <Sparkle size={12} weight="fill" />
            AI drafting: {ira.suggest_all_progress.done} / {ira.suggest_all_progress.queued}
            {ira.suggest_all_progress.errors ? <span className="text-red-700">({ira.suggest_all_progress.errors} err)</span> : null}
          </span>
        )}
      </div>

      {/* Sections */}
      <div className="border border-border divide-y divide-border" data-testid="ira-sections">
        {sections.map((s) => {
          const val = ira.answers?.[s.key] || "";
          const isOpen = !!open[s.key];
          const isDirty = !!dirty[s.key];
          return (
            <div key={s.key} data-testid={`ira-section-${s.key}`}>
              <button type="button"
                      className="w-full flex items-start gap-3 px-4 py-3 hover:bg-secondary/40 bg-transparent text-left"
                      onClick={() => setOpen((o) => ({ ...o, [s.key]: !o[s.key] }))}
                      data-testid={`ira-toggle-${s.key}`}>
                {isOpen ? <CaretDown size={18} className="mt-0.5 shrink-0" /> : <CaretRight size={18} className="mt-0.5 shrink-0" />}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{s.title}</span>
                    {val.trim() && <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">Started</span>}
                    {isDirty && <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Unsaved</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.help}</p>
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pl-11">
                  <Label htmlFor={`ira-${s.key}`} className="sr-only">{s.title}</Label>
                  <Textarea id={`ira-${s.key}`}
                            value={val}
                            onChange={(e) => setAnswer(s.key, e.target.value)}
                            rows={7}
                            disabled={!canEdit}
                            placeholder="Describe / list details for this section…"
                            data-testid={`ira-input-${s.key}`}
                            className="rounded-none font-mono text-xs" />
                  {canEdit && (
                    <div className="flex justify-end gap-2 mt-2">
                      <Button variant="outline" size="sm"
                              onClick={() => suggestSection(s.key)}
                              disabled={busy === `suggest-${s.key}`}
                              data-testid={`ira-suggest-${s.key}-btn`}>
                        <Sparkle size={14} className="mr-1.5" />
                        {busy === `suggest-${s.key}` ? "Drafting…" : "AI draft"}
                      </Button>
                      <Button variant="outline" size="sm"
                              onClick={() => saveSection(s.key)}
                              disabled={busy === `save-${s.key}` || !isDirty}
                              data-testid={`ira-save-${s.key}-btn`}>
                        {busy === `save-${s.key}` ? "Saving…" : "Save section"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* COP panel */}
      <div className="border border-border p-4 bg-emerald-50/40" data-testid="ira-cop-panel">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div>
            <h3 className="font-semibold text-sm">Compliance Oversight Plan (COP)</h3>
            <p className="text-xs text-muted-foreground">Paste or transcribe the Regional Entity&apos;s returned COP here. Kept alongside the IRA for audit continuity.</p>
          </div>
          {canEdit && (
            <Button onClick={saveCop} variant="outline" size="sm" data-testid="ira-cop-save-btn" disabled={busy === "cop"}>
              <FloppyDisk size={14} className="mr-1.5" /> Save COP
            </Button>
          )}
        </div>
        <Textarea value={ira.cop_notes || ""}
                  onChange={(e) => setIra((p) => ({ ...p, cop_notes: e.target.value }))}
                  rows={10}
                  disabled={!canEdit}
                  placeholder="Regional Entity&apos;s Compliance Oversight Plan — engagements, oversight cadence, and follow-up items."
                  data-testid="ira-cop-input"
                  className="rounded-none text-sm" />
      </div>

      {/* Applicability snapshot preview */}
      <details className="border border-border p-4" data-testid="ira-applicability-preview">
        <summary className="cursor-pointer text-sm font-semibold">
          Applicable Standards & Requirements snapshot ({distinctStds} standards • {totalReqs} requirements)
        </summary>
        <div className="mt-3 max-h-96 overflow-auto">
          <table className="w-full text-xs font-mono">
            <thead className="bg-secondary sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5">Standard</th>
                <th className="text-left px-2 py-1.5">Req</th>
                <th className="text-left px-2 py-1.5">Title</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-left px-2 py-1.5">Functions</th>
              </tr>
            </thead>
            <tbody>
              {(ira.applicability_snapshot || []).slice(0, 400).map((it, idx) => (
                <tr key={`${it.standard_id}-${it.req_number}-${idx}`} className="border-t border-border">
                  <td className="px-2 py-1">{it.standard_id}</td>
                  <td className="px-2 py-1">{it.req_number || "—"}</td>
                  <td className="px-2 py-1 truncate max-w-[26rem]" title={it.standard_title}>{it.standard_title}</td>
                  <td className="px-2 py-1">
                    {(it.enforceability || "enforceable").startsWith("retired") ? (
                      <span className="text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-300 px-1.5 py-0.5" title={it.enforceability}>Retired</span>
                    ) : (it.enforceability || "").startsWith("future") ? (
                      <span className="text-[10px] uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5" title={it.enforceability}>Future</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">Enforceable</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-muted-foreground">{(it.applicable_functions || []).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalReqs > 400 && <p className="text-xs text-muted-foreground mt-2">Showing first 400 of {totalReqs}. Full list is in the Excel export.</p>}
        </div>
      </details>
    </div>
  );
}
